import React, { useEffect, useState } from 'react';
import { Button, Checkbox, Modal, Spin } from 'antd';
import { FileTextOutlined } from '@ant-design/icons';
import LegalLoginShell from 'components/LegalLoginShell';
import Auth from 'auth';
import { handleApiError, handleApiSuccess } from 'utils';
import {
  usersAcceptLegalService,
  usersVerifyTokenService,
} from 'services/user';
import { ADDITIONAL_CONSENT_LABEL } from 'legal';
import termsOfService from 'legal/termsOfService';
import privacyPolicy from 'legal/privacyPolicy';
import LegalDocument from 'legal/LegalDocument';

const requireAdditional = Boolean(ADDITIONAL_CONSENT_LABEL);

function AcceptTerms() {
  const [isChecking, setIsChecking] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tosAccepted, setTosAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [additionalAccepted, setAdditionalAccepted] = useState(false);
  const [viewer, setViewer] = useState(null);

  useEffect(() => {
    if (!Auth.isAuthenticated()) {
      window.location.href = '/login';
      return;
    }

    usersVerifyTokenService()
      .then((response) => {
        if (response.data.item && response.data.item.legal_accepted_at) {
          window.location.href = '/';
          return;
        }
        setIsChecking(false);
      })
      .catch((error) => {
        setIsChecking(false);
        handleApiError(error);
      });
  }, []);

  const canSubmit =
    tosAccepted && privacyAccepted && (!requireAdditional || additionalAccepted);

  const onSubmit = () => {
    if (!canSubmit) {
      return;
    }
    setIsSubmitting(true);
    usersAcceptLegalService({
      tosAccepted: true,
      privacyAccepted: true,
      additionalAccepted: additionalAccepted || false,
    })
      .then((response) => {
        handleApiSuccess(response);
        const next = sessionStorage.getItem('postLegalRedirect') || '/';
        sessionStorage.removeItem('postLegalRedirect');
        window.location.href = next;
      })
      .catch((error) => {
        setIsSubmitting(false);
        handleApiError(error);
      });
  };

  const onSignOut = () => {
    Auth.signout();
    window.location.href = '/login';
  };

  return (
    <LegalLoginShell>
      <div className="legal-accept-card">
        {isChecking ? (
          <div className="legal-accept-loading">
            <Spin size="large" />
          </div>
        ) : (
          <>
            <div className="legal-accept-header">
              <div className="legal-accept-icon">
                <FileTextOutlined />
              </div>
              <h1>Accept Terms of Service</h1>
              <p className="legal-accept-intro">
                Review and accept the Terms of Service and Privacy Policy to
                continue. You only need to do this once.
              </p>
            </div>

            <div className="legal-scroll-box">
              <LegalDocument sections={termsOfService} />
            </div>

            <div className="legal-accept-links">
              <button
                type="button"
                className="legal-text-link"
                onClick={() => setViewer('terms')}
              >
                View Terms of Service
              </button>
              <span aria-hidden="true">·</span>
              <button
                type="button"
                className="legal-text-link"
                onClick={() => setViewer('privacy')}
              >
                View Privacy Policy
              </button>
            </div>

            <Modal
              visible={Boolean(viewer)}
              title={
                viewer === 'privacy'
                  ? 'Privacy Policy'
                  : 'Terms of Service'
              }
              onCancel={() => setViewer(null)}
              footer={
                <Button
                  type="primary"
                  shape="round"
                  onClick={() => setViewer(null)}
                >
                  Close
                </Button>
              }
              width={720}
              centered
              className="legal-view-modal"
              bodyStyle={{ maxHeight: '60vh', overflowY: 'auto' }}
            >
              <LegalDocument
                sections={
                  viewer === 'privacy' ? privacyPolicy : termsOfService
                }
              />
            </Modal>

            <div className="legal-accept-checks">
              <Checkbox
                checked={tosAccepted}
                onChange={(e) => setTosAccepted(e.target.checked)}
              >
                I have read and agree to the Terms of Service
              </Checkbox>
              <Checkbox
                checked={privacyAccepted}
                onChange={(e) => setPrivacyAccepted(e.target.checked)}
              >
                I have read and agree to the Privacy Policy
              </Checkbox>
              {requireAdditional && (
                <Checkbox
                  checked={additionalAccepted}
                  onChange={(e) => setAdditionalAccepted(e.target.checked)}
                >
                  {ADDITIONAL_CONSENT_LABEL}
                </Checkbox>
              )}
            </div>

            <Button
              className="legal-accept-submit"
              type="primary"
              shape="round"
              block
              disabled={!canSubmit}
              loading={isSubmitting}
              onClick={onSubmit}
            >
              Accept and Continue
            </Button>
            <Button type="link" onClick={onSignOut} className="legal-signout">
              Sign out
            </Button>
          </>
        )}
      </div>
    </LegalLoginShell>
  );
}

export default AcceptTerms;
